'use client';

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { uploadFileToR2 } from '@/lib/uploadUtils';
import { FileUploadProgress, UploadSessionState } from '@/types/uploadProgress';
import UploadProgressModal from '../UploadProgressModal';

import {
  DEFAULT_PREFERENCES,
  PreferenceState,
  EDITING_STYLE_GROUP,
  DETAIL_PREFERENCE_GROUPS,
  preferenceLabel,
  OrderFlow,
  PRICE_PER_FINAL_HDR_IMAGE,
  HDR_BRACKET_SIZES,
  HDR_MIN_FILES,
  HDR_MAX_FILES,
  STANDARD_TURNAROUND_HOURS,
  RUSH_TURNAROUND_HOURS,
  computeRushFee,
} from './config';
import StepIndicator, { OrderStep } from './StepIndicator';
import ServiceRail from './ServiceRail';
import PreferenceGroup from './PreferenceGroup';
import ToggleCard from './ToggleCard';
import UploadZone, { LocalFile } from './UploadZone';
import UploadPreviewGrid from './UploadPreviewGrid';
import NotesField from './NotesField';
import OrderSummary, { SummaryRow } from './OrderSummary';

interface WorkspaceService {
  id: string;
  serviceIdForOrder?: string;
  name: string;
  tagline: string;
  icon: string;
  flow: OrderFlow;
  price?: number;
  allowsReference?: boolean;
  requiresReference?: boolean;
}

const HDR_SERVICE: WorkspaceService = {
  id: 'hdr',
  name: 'HDR Processing',
  tagline: 'Best for bracketed exposures',
  icon: 'SparklesIcon',
  flow: 'hdr',
};

function iconForService(name: string): string {
  const n = name.toLowerCase();
  if (n.includes('dusk') || n.includes('twilight')) return 'MoonIcon';
  if (n.includes('removal') || n.includes('object')) return 'TrashIcon';
  if (n.includes('stag')) return 'HomeModernIcon';
  if (n.includes('portrait') || n.includes('retouch')) return 'UserIcon';
  if (n.includes('floor') || n.includes('plan')) return 'Squares2X2Icon';
  if (n.includes('sky')) return 'CloudIcon';
  if (n.includes('enhance')) return 'AdjustmentsHorizontalIcon';
  return 'PhotoIcon';
}

export default function CreateOrderWorkspace() {
  const router = useRouter();
  const { user } = useAuth();

  const [isHydrated, setIsHydrated] = useState(false);
  const [services, setServices] = useState<WorkspaceService[]>([HDR_SERVICE]);
  const [selectedServiceId, setSelectedServiceId] = useState<string>('hdr');

  const [preferences, setPreferences] = useState<PreferenceState>(DEFAULT_PREFERENCES);
  const [bracketSize, setBracketSize] = useState<number>(3);
  const [files, setFiles] = useState<LocalFile[]>([]);
  const [referenceFiles, setReferenceFiles] = useState<LocalFile[]>([]);
  const [notes, setNotes] = useState('');
  const [rush, setRush] = useState(false);

  const [error, setError] = useState('');
  const [showReview, setShowReview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successOrder, setSuccessOrder] = useState('');

  const [uploadSession, setUploadSession] = useState<UploadSessionState>({
    isActive: false,
    files: [],
    currentFileIndex: 0,
    overallProgress: 0,
    startTime: 0,
  });

  useEffect(() => setIsHydrated(true), []);

  useEffect(() => {
    if (isHydrated && !user) router.push('/auth/signin');
  }, [isHydrated, user, router]);

  // Fetch standard services from Supabase (unchanged query shape).
  useEffect(() => {
    (async () => {
      try {
        const { data, error: err } = await supabase
          .from('service_options')
          .select('id, service_id, name, description, active, price, services(allows_reference, requires_reference)')
          .eq('is_default', true)
          .eq('active', true)
          .order('display_order');
        if (err) throw err;

        const mapped: WorkspaceService[] = (data || []).map((s: any) => {
          const svc = Array.isArray(s.services) ? s.services[0] : s.services;
          return {
            id: s.id,
            serviceIdForOrder: s.service_id,
            name: s.name,
            tagline: s.description || 'Professional editing',
            icon: iconForService(s.name),
            flow: 'standard' as OrderFlow,
            price: s.price ?? 0,
            allowsReference: svc?.allows_reference ?? false,
            requiresReference: svc?.requires_reference ?? false,
          };
        });

        setServices([HDR_SERVICE, ...mapped]);
      } catch {
        // Keep HDR-only if services fail to load.
      }
    })();
  }, []);

  // Warn before leaving during active upload.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (uploadSession.isActive) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploadSession.isActive]);

  const selected = useMemo(
    () => services.find((s) => s.id === selectedServiceId) || HDR_SERVICE,
    [services, selectedServiceId]
  );
  const isHdr = selected.flow === 'hdr';

  /* ----------------------------- pricing ----------------------------- */
  const estimatedSets = isHdr ? Math.floor(files.length / bracketSize) : files.length;
  const subtotal = isHdr
    ? estimatedSets * PRICE_PER_FINAL_HDR_IMAGE
    : (selected.price || 0) * files.length;
  const rushFee = rush ? computeRushFee(subtotal) : 0;
  const total = subtotal + rushFee;

  const deliveryLabel = rush ? '12 hours' : '24 hours';
  const estimatedOutput = isHdr ? `${estimatedSets} HDR sets` : `${files.length} images`;

  const canContinue = isHdr
    ? files.length >= HDR_MIN_FILES
    : Boolean(selected.serviceIdForOrder) &&
      files.length >= 1 &&
      (!selected.requiresReference || referenceFiles.length >= 1);

  /* --------------------------- summary rows --------------------------- */
  const summaryRows: SummaryRow[] = useMemo(() => {
    const rows: SummaryRow[] = [
      { label: 'Editing Style', value: preferenceLabel('editingStyle', preferences.editingStyle) },
    ];
    if (isHdr) {
      DETAIL_PREFERENCE_GROUPS.forEach((g) => {
        rows.push({ label: g.label, value: preferenceLabel(g.key, preferences[g.key] as string) });
      });
    }
    rows.push({ label: 'Minor Cleanup', value: preferences.minorCleanup ? 'Auto Remove' : 'Off' });
    return rows;
  }, [preferences, isHdr]);

  /* ------------------------------ helpers ----------------------------- */
  const setPref = (key: string, value: string) =>
    setPreferences((p) => ({ ...p, [key]: value }));

  const addFiles = useCallback((incoming: LocalFile[]) => {
    setError('');
    setFiles((prev) => [...prev, ...incoming]);
  }, []);

  const removeFile = (id: string) => {
    setFiles((prev) => {
      const f = prev.find((x) => x.id === id);
      if (f?.preview) URL.revokeObjectURL(f.preview);
      return prev.filter((x) => x.id !== id);
    });
  };

  const addReferences = useCallback((incoming: LocalFile[]) => {
    setReferenceFiles((prev) => [...prev, ...incoming].slice(0, 6));
  }, []);

  const removeReference = (id: string) => {
    setReferenceFiles((prev) => {
      const f = prev.find((x) => x.id === id);
      if (f?.preview) URL.revokeObjectURL(f.preview);
      return prev.filter((x) => x.id !== id);
    });
  };

  const switchService = (id: string) => {
    setSelectedServiceId(id);
    setError('');
  };

  // Live list of "Label: Value" preference lines shown to the customer and sent to editors.
  const preferenceLines = useMemo(() => {
    const lines: { label: string; value: string }[] = [
      { label: 'Editing Style', value: preferenceLabel('editingStyle', preferences.editingStyle) },
    ];
    if (isHdr) {
      DETAIL_PREFERENCE_GROUPS.forEach((g) => {
        lines.push({ label: g.label, value: preferenceLabel(g.key, preferences[g.key] as string) });
      });
    }
    lines.push({ label: 'Minor Cleanup', value: preferences.minorCleanup ? 'Auto Remove' : 'Off' });
    return lines;
  }, [preferences, isHdr]);

  const buildPreferenceNotes = (): string => {
    const lines = preferenceLines.map((l) => `${l.label}: ${l.value}`);
    if (rush) lines.push('Delivery: RUSH (12h)');
    return lines.join('\n');
  };

  /* ------------------------------ submit ------------------------------ */
  const runUploads = async (
    orderId: string,
    originals: LocalFile[],
    refs: LocalFile[]
  ): Promise<string | null> => {
    const all: FileUploadProgress[] = [...originals, ...refs].map((f) => ({
      id: f.id,
      file: f.file,
      status: 'queued',
      progress: 0,
      bytesUploaded: 0,
      totalBytes: f.file.size,
      uploadSpeed: 0,
      retryCount: 0,
    }));

    setUploadSession({
      isActive: true,
      files: all,
      currentFileIndex: 0,
      overallProgress: 0,
      startTime: Date.now(),
    });

    let firstOriginalImageId: string | null = null;

    for (let i = 0; i < all.length; i++) {
      const isReference = i >= originals.length;
      setUploadSession((prev) => ({
        ...prev,
        currentFileIndex: i,
        files: prev.files.map((f, idx) => (idx === i ? { ...f, status: 'uploading' } : f)),
      }));

      const { imageId } = await uploadFileToR2({
        file: all[i].file,
        orderId,
        role: isReference ? 'reference' : 'raw',
        parentImageId: isReference && firstOriginalImageId ? firstOriginalImageId : undefined,
        onProgress: (progress) => {
          setUploadSession((prev) => {
            const updated = [...prev.files];
            updated[i] = {
              ...updated[i],
              progress,
              bytesUploaded: Math.round((progress / 100) * updated[i].totalBytes),
            };
            const overall = updated.reduce((s, f) => s + f.progress, 0) / updated.length;
            return { ...prev, files: updated, overallProgress: overall };
          });
        },
      });

      if (!isReference && !firstOriginalImageId) firstOriginalImageId = imageId;

      // Preserve upload ordering for originals.
      if (!isReference && imageId) {
        await supabase.from('images').update({ display_order: i + 1 }).eq('id', imageId);
      }

      setUploadSession((prev) => ({
        ...prev,
        files: prev.files.map((f, idx) =>
          idx === i ? { ...f, status: 'completed', progress: 100 } : f
        ),
      }));
    }

    setUploadSession((prev) => ({ ...prev, isActive: false }));
    return firstOriginalImageId;
  };

  const handlePlaceOrder = async () => {
    if (!user || !canContinue || isSubmitting) return;
    setIsSubmitting(true);
    setError('');

    const now = new Date();
    const deadlineHours = rush ? RUSH_TURNAROUND_HOURS : STANDARD_TURNAROUND_HOURS;
    const deadline = new Date(now.getTime() + deadlineHours * 60 * 60 * 1000);
    const prefNotes = buildPreferenceNotes();
    const combinedNotes = [prefNotes, notes.trim()].filter(Boolean).join('\n\n');

    try {
      if (isHdr) {
        const orderNumber = `ORD-HDR-${Date.now()}-${Math.floor(Math.random() * 10000)}`;

        const { data, error: orderErr } = await supabase
          .from('orders')
          .insert([
            {
              client_id: user.id,
              order_number: orderNumber,
              title: `HDR Processing - ${orderNumber}`,
              service_type: 'HDR Processing',
              status: 'pending',
              total_images: files.length,
              budget: total,
              deadline: deadline.toISOString(),
              special_instructions: `HDR Processing - Estimated ${estimatedSets} final images\nUploaded images: ${files.length}\nExposures per image: ${bracketSize}\n\n${combinedNotes}`,
              created_at: now.toISOString(),
              hdr_metadata: JSON.stringify({
                uploaded_image_count: files.length,
                estimated_exposures_per_image: bracketSize,
                estimated_final_images: estimatedSets,
                estimated_price: subtotal,
                rush: rush,
                rush_fee: rushFee,
                preferences,
                price_status: 'estimated',
                final_images: null,
                final_price: null,
              }),
            },
          ])
          .select();

        if (orderErr) throw new Error(orderErr.message);
        const orderId = data?.[0]?.id;
        if (!orderId) throw new Error('Order created but ID not returned');

        await runUploads(orderId, files, []);

        await notify(orderNumber, 'HDR Processing', files.length, total, `HDR (${estimatedSets} estimated final images)`);
        finish(orderNumber);
      } else {
        const orderNumber = `ORD-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
        const { data, error: orderErr } = await supabase
          .from('orders')
          .insert([
            {
              client_id: user.id,
              service_id: selected.serviceIdForOrder,
              order_number: orderNumber,
              title: `${selected.name} - ${orderNumber}`,
              service_type: 'standard_editing',
              order_type: 'standard',
              status: 'pending',
              total_images: files.length,
              budget: total,
              deadline: deadline.toISOString(),
              special_instructions: combinedNotes || null,
              created_at: new Date().toISOString(),
            },
          ])
          .select();

        if (orderErr) throw new Error(orderErr.message);
        const orderId = data?.[0]?.id;
        if (!orderId) throw new Error('Order created but ID not returned');

        await runUploads(orderId, files, referenceFiles);

        await notify(orderNumber, selected.name, files.length, total, 'Standard Editing');
        finish(orderNumber);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to create order. Please try again.');
      setUploadSession((prev) => ({ ...prev, isActive: false }));
      setIsSubmitting(false);
    }
  };

  const notify = async (
    orderNumber: string,
    serviceName: string,
    totalImages: number,
    totalPrice: number,
    orderType: string
  ) => {
    try {
      await fetch('/api/order-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderNumber,
          serviceName,
          clientEmail: user?.email || 'Unknown',
          totalImages,
          totalPrice,
          orderType,
        }),
      });
    } catch {
      /* non-blocking */
    }
  };

  const finish = (orderNumber: string) => {
    setSuccessOrder(orderNumber);
    setShowReview(false);
    setTimeout(() => router.push('/client-dashboard'), 2500);
  };

  /* ------------------------------ render ------------------------------ */
  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-[#0B0D17] flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0D17]">
      {/* Header */}
      <div className="border-b border-white/[0.06] bg-[#0B0D17]/90 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => router.back()}
              className="text-gray-500 hover:text-white transition-colors flex items-center gap-1.5 text-xs font-medium flex-shrink-0"
            >
              <Icon name="ChevronLeftIcon" size={16} variant="outline" />
              New Order
            </button>
            <span className="w-px h-5 bg-white/10 hidden sm:block" />
            <h1 className="text-lg sm:text-xl font-bold text-white truncate">Create New Order</h1>
            <span className="hidden md:inline-flex items-center px-2.5 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-[11px] font-semibold text-purple-300">
              {selected.name}
            </span>
          </div>
          <StepIndicator current={'upload' as OrderStep} />
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* ---------------- Left workspace ---------------- */}
          <div className="lg:col-span-8 space-y-2.5 pb-24 lg:pb-0">
            {/* 1. Service selection */}
            <section className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-3.5">
              <SectionHeading index={1} title="Choose Service" />
              <ServiceRail
                services={services}
                selectedId={selectedServiceId}
                onSelect={switchService}
                popularCount={6}
              />
            </section>

            {/* 2. Preferences */}
            <section className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-3.5">
              <SectionHeading index={2} title="Preferences" subtitle="Choose the result you want — we handle the how." />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5">
                <PreferenceGroup
                  group={EDITING_STYLE_GROUP}
                  value={preferences.editingStyle}
                  onChange={(v) => setPref('editingStyle', v)}
                />
                {isHdr &&
                  DETAIL_PREFERENCE_GROUPS.map((g) => (
                    <PreferenceGroup
                      key={g.key}
                      group={g}
                      value={preferences[g.key] as string}
                      onChange={(v) => setPref(g.key, v)}
                    />
                  ))}
              </div>

              <div className="mt-3">
                <ToggleCard
                  title="Minor Cleanup"
                  description="Remove small distractions — cords, outlet covers, floor debris, tiny wall marks."
                  icon="SparklesIcon"
                  checked={preferences.minorCleanup}
                  onChange={(v) => setPreferences((p) => ({ ...p, minorCleanup: v }))}
                />
              </div>
            </section>

            {/* 3. Upload (hero) */}
            <section className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-3.5">
              <SectionHeading
                index={3}
                title={isHdr ? 'Upload Bracketed Images' : 'Upload Images'}
                subtitle={
                  isHdr
                    ? 'Upload 3 or more bracketed exposures of the same scene.'
                    : 'Upload the photos you want us to edit.'
                }
              />

              <UploadZone
                onFilesAdded={addFiles}
                onError={setError}
                maxFiles={isHdr ? HDR_MAX_FILES : 300}
                currentCount={files.length}
                minFiles={isHdr ? HDR_MIN_FILES : 1}
              />

              {/* HDR bracket size */}
              {isHdr && files.length >= HDR_MIN_FILES && (
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <span className="text-xs text-gray-500">Exposures per set</span>
                  <div className="inline-flex gap-1.5 p-1 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                    {HDR_BRACKET_SIZES.map((n) => (
                      <button
                        key={n}
                        onClick={() => setBracketSize(n)}
                        className={`relative w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${
                          bracketSize === n ? 'text-white' : 'text-gray-400 hover:text-gray-200'
                        }`}
                      >
                        {bracketSize === n && (
                          <motion.span
                            layoutId="bracket-pill"
                            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                            className="absolute inset-0 rounded-lg bg-purple-600"
                          />
                        )}
                        <span className="relative z-10">{n}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* live HDR estimate */}
              {isHdr && files.length > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <Stat label="Images Uploaded" value={String(files.length)} />
                  <Stat label="Estimated HDR Sets" value={String(estimatedSets)} />
                  <Stat label="Estimated Price" value={`$${subtotal.toFixed(2)}`} accent />
                </div>
              )}

              {/* previews */}
              {files.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2.5">
                    <p className="text-xs font-semibold text-gray-300">
                      {files.length} file{files.length === 1 ? '' : 's'} ready
                    </p>
                    <button
                      onClick={() => setFiles([])}
                      className="text-[11px] text-gray-500 hover:text-red-400 transition-colors"
                    >
                      Clear all
                    </button>
                  </div>
                  <UploadPreviewGrid files={files} onRemove={removeFile} />
                </div>
              )}

              {/* reference upload for standard services */}
              {!isHdr && (selected.allowsReference || selected.requiresReference) && (
                <div className="mt-5 pt-5 border-t border-white/[0.06]">
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-sm font-semibold text-gray-200">Reference Images</span>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                        selected.requiresReference
                          ? 'bg-red-500/10 text-red-300 border-red-500/30'
                          : 'bg-blue-500/10 text-blue-300 border-blue-500/30'
                      }`}
                    >
                      {selected.requiresReference ? 'REQUIRED' : 'OPTIONAL · MAX 6'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Examples that show the look you want — these guide our editors and are not edited.
                  </p>
                  {referenceFiles.length < 6 && (
                    <UploadZone
                      onFilesAdded={addReferences}
                      onError={setError}
                      maxFiles={6}
                      currentCount={referenceFiles.length}
                      compact
                      label="Add reference images"
                    />
                  )}
                  {referenceFiles.length > 0 && (
                    <div className="mt-3">
                      <UploadPreviewGrid files={referenceFiles} onRemove={removeReference} />
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="mt-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
                  {error}
                </div>
              )}

              {/* Instructions — preferences + free text, exactly what the editor receives */}
              <div className="mt-3 pt-3 border-t border-white/[0.06]">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Icon name="PencilSquareIcon" size={13} variant="outline" className="text-gray-500" />
                  <span className="text-xs font-semibold text-gray-300">Instructions for your editor</span>
                </div>

                {/* Live, read-only preview of the selected preferences (always included). */}
                <div className="mb-2 rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1.5">
                    Your preferences (added automatically)
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {preferenceLines.map((l) => (
                      <span
                        key={l.label}
                        className="inline-flex items-center gap-1 rounded-md bg-purple-500/10 border border-purple-500/20 px-2 py-0.5 text-[11px]"
                      >
                        <span className="text-gray-500">{l.label}:</span>
                        <span className="font-semibold text-purple-200">{l.value}</span>
                      </span>
                    ))}
                    {rush && (
                      <span className="inline-flex items-center gap-1 rounded-md bg-amber-400/10 border border-amber-400/20 px-2 py-0.5 text-[11px]">
                        <span className="text-gray-500">Delivery:</span>
                        <span className="font-semibold text-amber-200">Rush (12h)</span>
                      </span>
                    )}
                  </div>
                </div>

                <NotesField value={notes} onChange={setNotes} />
              </div>
            </section>
          </div>

          {/* ---------------- Right sticky sidebar ---------------- */}
          <div className="lg:col-span-4 hidden lg:block">
            <div className="sticky top-24">
              <OrderSummary
                serviceName={selected.name}
                rows={summaryRows}
                imagesUploaded={files.length}
                estimatedOutput={estimatedOutput}
                deliveryLabel={deliveryLabel}
                price={total}
                priceIsEstimate={isHdr}
                rushEnabled={rush}
                rushFee={rush ? rushFee : computeRushFee(subtotal)}
                onRushToggle={setRush}
                canContinue={canContinue}
                onContinue={() => setShowReview(true)}
                isSubmitting={isSubmitting}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky CTA */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#0B0D17]/95 backdrop-blur-xl border-t border-white/[0.08] px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] text-gray-500">{isHdr ? 'Est. total' : 'Total'}</p>
            <p className="text-lg font-bold text-white">${total.toFixed(2)}</p>
          </div>
          <button
            onClick={() => setShowReview(true)}
            disabled={!canContinue}
            className={`flex-1 max-w-[220px] py-3 rounded-xl font-bold text-sm ${
              canContinue
                ? 'bg-gradient-to-r from-purple-600 to-purple-500 text-white'
                : 'bg-white/[0.05] text-gray-500'
            }`}
          >
            Continue to Review
          </button>
        </div>
      </div>

      {/* Review modal */}
      <AnimatePresence>
        {showReview && !uploadSession.isActive && !successOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0B0D17]/85 backdrop-blur-md"
            onClick={() => !isSubmitting && setShowReview(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ type: 'spring', stiffness: 300, damping: 26 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md rounded-2xl border border-white/[0.12] bg-[#13151F] shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
            >
              <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
              <div className="p-6 border-b border-white/[0.08]">
                <h3 className="text-lg font-bold text-white">Review your order</h3>
                <p className="text-sm text-gray-400">Confirm the details before we start.</p>
              </div>
              <div className="p-6 space-y-2.5 max-h-[50vh] overflow-y-auto">
                <ReviewRow label="Service" value={selected.name} />
                {summaryRows.map((r) => (
                  <ReviewRow key={r.label} label={r.label} value={r.value} />
                ))}
                <ReviewRow label="Images Uploaded" value={String(files.length)} />
                <ReviewRow label="Estimated Output" value={estimatedOutput} />
                <ReviewRow label="Delivery" value={deliveryLabel} />
                <div className="flex items-center justify-between pt-3 mt-1 border-t border-white/[0.08]">
                  <span className="text-white font-semibold">{isHdr ? 'Estimated Maximum' : 'Total'}</span>
                  <span className="text-2xl font-bold bg-gradient-to-r from-purple-300 to-purple-400 bg-clip-text text-transparent">
                    ${total.toFixed(2)}
                  </span>
                </div>
                {isHdr && (
                  <div className="flex items-center gap-1.5 text-xs text-purple-200 bg-purple-500/10 border border-purple-500/20 rounded-lg px-3 py-2">
                    <span>💜</span>
                    <span>Final price may be lower after review.</span>
                    <span className="relative group/tip ml-0.5 inline-flex items-center">
                      <Icon
                        name="InformationCircleIcon"
                        size={14}
                        variant="outline"
                        className="text-purple-300 cursor-help"
                      />
                      <span className="pointer-events-none absolute bottom-full right-0 mb-2 w-64 rounded-lg bg-[#0B0D17] border border-white/[0.12] p-3 text-[11px] leading-relaxed text-gray-300 opacity-0 group-hover/tip:opacity-100 transition-opacity shadow-xl z-20">
                        This is an estimate based on your upload. Our editors review every order, and if your
                        images require less work than estimated, your final price is reduced before payment.
                      </span>
                    </span>
                  </div>
                )}
                {error && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-xs">
                    {error}
                  </div>
                )}
              </div>
              <div className="p-6 pt-0 flex gap-3">
                <button
                  onClick={() => setShowReview(false)}
                  disabled={isSubmitting}
                  className="flex-1 py-3 rounded-xl border border-white/[0.12] text-gray-200 font-semibold hover:bg-white/[0.06] disabled:opacity-50"
                >
                  Back to Edit
                </button>
                <button
                  onClick={handlePlaceOrder}
                  disabled={isSubmitting}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-purple-500 text-white font-bold shadow-lg shadow-purple-500/30 hover:from-purple-500 hover:to-purple-400 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Placing…
                    </>
                  ) : (
                    'Place Order'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload progress modal (reused from existing flow) */}
      <UploadProgressModal
        files={uploadSession.files}
        isOpen={uploadSession.isActive}
        overallProgress={uploadSession.overallProgress}
        onCancel={() => setUploadSession((p) => ({ ...p, isActive: false }))}
      />

      {/* Success overlay */}
      <AnimatePresence>
        {successOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-6 bg-[#0B0D17]/90 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24 }}
              className="relative text-center max-w-md rounded-2xl border border-white/[0.12] bg-[#13151F] p-10"
            >
              <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
                <Icon name="CheckCircleIcon" size={32} variant="solid" className="text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Order Created!</h2>
              <p className="text-gray-400 mb-1">{successOrder}</p>
              <p className="text-sm text-gray-500">Redirecting to your dashboard…</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------ small bits ------------------------------ */

function SectionHeading({ index, title, subtitle }: { index: number; title: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <span className="w-5 h-5 rounded-md bg-purple-500/15 text-purple-300 text-[11px] font-bold flex items-center justify-center flex-shrink-0">
        {index}
      </span>
      <h2 className="text-sm font-bold text-white leading-tight">{title}</h2>
      {subtitle && <span className="text-[11px] text-gray-500 truncate">· {subtitle}</span>}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5">
      <p className="text-[11px] text-gray-500 mb-0.5">{label}</p>
      <motion.p
        key={value}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className={`text-lg font-bold ${accent ? 'text-purple-300' : 'text-white'}`}
      >
        {value}
      </motion.p>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-200 text-right">{value}</span>
    </div>
  );
}
